import type { ObservableMap } from 'mobx'
import { observable } from 'mobx'
import type { TranscriptMessage } from '../../types'
import type { MeetingCubit } from '../MeetingCubit'
import { SlideType } from '../../firestore/Slide/types'
import { SERVER_CLOCK_WAIT_MS } from '../../models/ServerClock'

export class SlideTimerController {
  meeting: MeetingCubit
  _messages: ObservableMap<number, TranscriptMessage> = observable.map({})

  slideTimer: NodeJS.Timeout | null = null
  _clockOffsetWaitStartedAt: number | null = null
  debugMode = false

  constructor(meeting: MeetingCubit, options: { debug?: boolean } = {}) {
    this.meeting = meeting
    this.debugMode = options.debug || false
  }

  dispose() {
    this.cancelSlideTimer()
  }

  cancelSlideTimer() {
    this.debugLog('cancelSlideTimer')
    if (this.slideTimer) {
      clearInterval(this.slideTimer)
    }
  }

  resetSlideTimer() {
    this.debugLog('resetSlideTimer')
    this.meeting.updateProgressBar(0)
    this.cancelSlideTimer()
    // Each slide gets its own wait for a pending server clock.
    this._clockOffsetWaitStartedAt = null

    if (
      this.meeting.currentSlide?.type !== SlideType.video &&
      this.meeting.currentSlide?.duration === 0
    ) {
      this.meeting.updateProgressBar(1)
    } else {
      this.calculateSlideTimer()
      this.slideTimer = setInterval(() => {
        this.calculateSlideTimer()
      }, 1000)
    }
  }

  calculateSlideTimer() {
    this.debugLog('calculateSlideTimer')
    const currentSlide = this.meeting.currentSlide

    // Every video slide drives its own progress from the real player position:
    // uploaded videos via local playback, and YouTube embeds via
    // the IFrame Player API (BL-4662). Approximating YouTube progress from a
    // wall-clock timer here desynced the Next-button gate from what the viewer
    // saw — buffering, a paused/rewatched video, or a late joiner bumping
    // activeSlideChangedAt could leave Next stuck disabled after the video ended.
    const isVideoSlide = currentSlide?.type === SlideType.video
    // Dynamic Intro Video advances when its video ends (handled by the
    // renderer), so — like a video slide — the timer must not auto-advance it.
    const slideControlsProgress =
      isVideoSlide || currentSlide?.type === SlideType.dynamicIntroVideo

    if (slideControlsProgress) return

    const slideDuration = currentSlide?.duration
    const slideChangedAt = this.meeting.activeSlideChangedAt
    if (!slideChangedAt || !slideDuration) {
      this.debugLog('slideChangedAt or slideDuration is not set', {
        slideChangedAt,
        slideDuration,
      })
      return
    }

    // On rejoin the server clock may still be measuring. Drawing now would
    // flash a countdown computed from a skewed device clock. Stay in the
    // loading state (leave the progress bar untouched) until it finishes, but
    // no longer than a short grace window, so the timer never hangs if it never
    // completes. The next tick recomputes.
    const clock = this.meeting.repository.serverClock
    if (clock.status === 'pending') {
      this._clockOffsetWaitStartedAt ??= Date.now()
      if (Date.now() - this._clockOffsetWaitStartedAt < SERVER_CLOCK_WAIT_MS) {
        this.debugLog('server clock pending; deferring slide-timer draw')
        return
      }
    } else {
      this._clockOffsetWaitStartedAt = null
    }

    // slideChangedAt is a server timestamp, so elapsed must be measured on the
    // server clock, not Date.now().
    const elapsedSeconds = (clock.now() - slideChangedAt.getTime()) / 1000
    const slideExpired = elapsedSeconds >= slideDuration

    // Past the grace window on an uncorrected clock that runs behind, elapsed
    // can be negative. Clamp so the bar never goes below empty.
    const currentProgress = Math.max(elapsedSeconds / slideDuration, 0)

    // updateProgressBar caps at 1, so an expired slide draws a full bar.
    this.meeting.updateProgressBar(
      currentProgress === 0 ? 0.0001 : currentProgress
    )

    // endTimer cancels the interval, so the slide can never be redrawn after
    // it. While the clock is still pending (past the grace window), a fast
    // device clock may make the slide read as expired only until the
    // correction lands, so keep ticking. Once the clock is trusted, or
    // untrusted (measuring finished without a correction, so the device clock
    // is all there is), end it.
    if (slideExpired) {
      if (clock.status === 'pending') {
        this.debugLog('server clock pending; slide not ended yet')
      } else {
        this.endTimer()
      }
    }

    this.debugLog({ slideDuration, elapsedSeconds })
  }

  endTimer() {
    this.cancelSlideTimer()
    this.meeting.updateProgressBar(1)
  }

  debugLog(...args: unknown[]) {
    if (this.debugMode) {
      // eslint-disable-next-line no-console
      console.log(...args)
    }
  }
}
